import React, { memo } from 'react'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import EllipsisIcon from 'transfix-icons/build/EllipsisIcon'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import FormattedPhoneNumber from 'transfix-ui/components/FormattedPhoneNumber'
import { IconButton } from 'transfix-ui/components/IconButton'
import { RelativeDateTime } from 'transfix-ui/components/RelativeDateTime'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Box, Typography } from 'transfix-ui/core'
import { ArrayElement } from 'types'
import { LaneAlertResponse } from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import LaneAlertPickupAvailability from 'apps/marketplace/components/LaneAlertPickupAvailability'
import { OverflowMenu, useOverflowToggle, Avatar, ContextCard } from 'transfix-ui'
import { EQUIPMENT } from 'constants/equipment'

interface ILaneAlertTableEntryRowProps {
  laneAlert: ArrayElement<LaneAlertResponse['laneAlerts']>
  onOpenDeleteModal?: (id: number) => void
  onOpenEditModal?: (id: ArrayElement<LaneAlertResponse['laneAlerts']>) => void
}

export const LaneAlertTableEntryRow = memo<ILaneAlertTableEntryRowProps>(
  ({ laneAlert, onOpenDeleteModal, onOpenEditModal }) => {
    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
    const overflowOptions = [
      {
        itemComponent: (
          <Box display='flex' alignItems='items-center'>
            <EditIcon size='large' />
            <Box display='inline' ml={2}>
              Edit Lane Alert
            </Box>
          </Box>
        ),
        label: 'edit',
        action: () => {
          if (onOpenEditModal) {
            onOpenEditModal(laneAlert)
          }
        },
      },
      {
        itemComponent: (
          <Box display='flex' alignItems='items-center'>
            <DeleteIcon size='large' />
            <Box display='inline' ml={2}>
              Delete Lane Alert
            </Box>
          </Box>
        ),
        label: 'delete',
        action: () => {
          if (onOpenDeleteModal) {
            onOpenDeleteModal(laneAlert.id)
          }
        },
      },
    ]

    const {
      createdAt,
      createdBy,
      deliveryCity,
      deliveryMiles,
      deliveryStateCode,
      id,
      pickupAvailability,
      pickupCity,
      pickupEndDate,
      pickupMiles,
      pickupStartDate,
      pickupStateCode,
      recipientSubscribers,
      sourceType,
      trailerType,
    } = laneAlert || {}

    const { name: equipmentName } = EQUIPMENT.find(equip => equip.key === trailerType) || {}

    const recipient =
      recipientSubscribers && recipientSubscribers.length > 0 ? (
        <a href={`/users/${recipientSubscribers[0].parentUserId}`} target='_blank' rel='noreferrer'>
          {recipientSubscribers[0].firstName} {recipientSubscribers[0].lastName}
        </a>
      ) : (
        ''
      )

    const multipleRecipients =
      recipientSubscribers && recipientSubscribers.length > 1 ? (
        <ContextCard triggerText={`and ${recipientSubscribers.length - 1} more`}>
          <Box>
            <Typography variant='h3'>Recipients</Typography>
            {recipientSubscribers.map(subscriber => {
              const { email, firstName, lastName, parentUserId, phoneNumber } = subscriber || {}
              return (
                <Box key={lastName} pt={2}>
                  <Box>
                    <a href={`/users/${parentUserId}`} target='_blank' rel='noreferrer'>
                      {firstName} {lastName}
                    </a>
                  </Box>
                  <Box>
                    {phoneNumber && <FormattedPhoneNumber phoneNumber={phoneNumber} />}
                    &nbsp;&middot;&nbsp;
                    <a href={`mailto:${email}`}>{email}</a>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </ContextCard>
      ) : (
        ''
      )

    return (
      <TableRow key={id}>
        <TableCell>
          <Box>
            {pickupCity && pickupStateCode ? `${pickupCity}, ${pickupStateCode} ` : 'Anywhere'}{' '}
            {pickupMiles !== 0 && pickupCity && (
              <Typography variant='caption' color='textSecondary'>
                +{pickupMiles}
              </Typography>
            )}{' '}
            <LaneArrowIcon />{' '}
            {deliveryCity && deliveryStateCode
              ? `${deliveryCity}, ${deliveryStateCode}`
              : 'Anywhere'}{' '}
            {deliveryMiles !== 0 && deliveryCity && (
              <Typography variant='caption' color='textSecondary'>
                +{deliveryMiles}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant='subtitle2' color='textSecondary'>
              {equipmentName || 'Any Equipment'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <LaneAlertPickupAvailability
            pickupAvailability={pickupAvailability || []}
            pickupStartDate={pickupStartDate}
            pickupEndDate={pickupEndDate}
          />
        </TableCell>
        <TableCell>
          <Box>{recipient}</Box>
          {multipleRecipients}
        </TableCell>
        <TableCell>
          <Box display='flex'>
            <Box display='inline' mr={2}>
              <Avatar
                key={`${createdBy.firstName} ${createdBy.lastName}`}
                name={`${createdBy.firstName} ${createdBy.lastName}`}
              />
            </Box>
            <Box display='inline'>
              <Box>{`${createdBy.firstName} ${createdBy.lastName}`}</Box>
              <Box className='text-secondary'>
                Added <RelativeDateTime datetime={createdAt} inSentence />
              </Box>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          {sourceType !== 'dispatcher_created' && (
            <OverflowMenu
              onExpanded={setExpanded}
              expanded={expanded}
              dropdownId={String(id)}
              trigger={
                <IconButton
                  Icon={EllipsisIcon}
                  label='edit'
                  data-test='edit-lane-alert-row'
                  onClick={clickOverflowTrigger}
                  size='large'
                />
              }
              options={overflowOptions}
              dataTest='lane-alert-dropdown'
            />
          )}
        </TableCell>
      </TableRow>
    )
  }
)
